import React, { useEffect } from 'react';
import { Field, Form as FForm, Formik, useFormikContext } from 'formik';
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { CustomSelectComponent, FieldInput, YupString } from 'motion-components';
import { requiredString } from 'shared-pages/common/constants';
import { formatDate } from 'shared-pages/utils/helpers';

type LookupOption = { value: string; label: string; regionId?: number | null };

export type CollectionpointFormValues = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  addressArea: string;
  addressCity: string;
  regionId: string;
  subregionId: string;
  contactPerson: string;
  phone: string;
  mobile: string;
  email: string;
  companyVat: string;
  companyTaxOffice: string;
};

type Props = {
  canEdit: boolean;
  submitting: boolean;
  submitError?: string | null;
  saved?: boolean;
  expiresAt?: string | null;
  collectionpoint: CollectionpointFormValues;
  regions: LookupOption[];
  subregions: LookupOption[];
  onSubmit: (values: CollectionpointFormValues) => Promise<void>;
};

function toFormValues(cp: any): CollectionpointFormValues {
  return {
    name: cp?.name ?? '',
    addressLine1: cp?.addressLine1 ?? '',
    addressLine2: cp?.addressLine2 ?? '',
    postalCode: cp?.postalCode ?? '',
    addressArea: cp?.addressArea ?? '',
    addressCity: cp?.addressCity ?? '',
    regionId: cp?.regionId != null ? String(cp.regionId) : '',
    subregionId: cp?.subregionId != null ? String(cp.subregionId) : '',
    contactPerson: cp?.contactPerson ?? '',
    phone: cp?.phone ?? '',
    mobile: cp?.mobile ?? '',
    email: cp?.email ?? '',
    companyVat: cp?.companyVat ?? '',
    companyTaxOffice: cp?.companyTaxOffice ?? '',
  };
}

/** Αν αλλάξει περιφέρεια, καθαρίζει ενότητα που δεν της ανήκει */
const RegionSubregionSync = ({ subregions }: { subregions: LookupOption[] }) => {
  const { values, setFieldValue } = useFormikContext<CollectionpointFormValues>();
  useEffect(() => {
    if (!values.subregionId) return;
    const selected = subregions.find((s) => s.value === String(values.subregionId));
    if (selected && String(selected.regionId ?? '') !== String(values.regionId ?? '')) {
      setFieldValue('subregionId', '');
    }
  }, [values.regionId, values.subregionId, subregions, setFieldValue]);
  return null;
};

/** *****************************************************
 * Πεδία φόρμας επικαιροποίησης στοιχείων σημείου
 * ***************************************************** */
const Fields = ({
  canEdit,
  regions,
  subregions,
}: {
  canEdit: boolean;
  regions: LookupOption[];
  subregions: LookupOption[];
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<CollectionpointFormValues>();
  const subregionOptions = subregions.filter(
    (s) => !values.regionId || String(s.regionId) === String(values.regionId),
  );

  return (
    <>
      <RegionSubregionSync subregions={subregions} />
      <Row>
        <Col md={6}>
          <FieldInput
            label={t('Επωνυμία')}
            name="name"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Διεύθυνση')}
            name="addressLine1"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Διεύθυνση - 2η γραμμή')}
            name="addressLine2"
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput
            label={t('T.K.')}
            name="postalCode"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Περιοχή')}
            name="addressArea"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Πόλη')}
            name="addressCity"
            placeholder=""
            disabled={!canEdit}
            required
          />
        </Col>
        <Col md={6}>
          <Field
            className="mb-2"
            name="regionId"
            title={t('Περιφέρεια')}
            component={(props) => <CustomSelectComponent {...props} as="div" />}
            options={regions}
            disabled={!canEdit}
            required
          />
          <Field
            className="mb-2"
            name="subregionId"
            title={t('Περιφερειακή Ενότητα')}
            component={(props) => <CustomSelectComponent {...props} as="div" />}
            options={subregionOptions}
            disabled={!canEdit || !values.regionId}
            required
          />
          <FieldInput
            label={t('Υπεύθυνος')}
            name="contactPerson"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Τηλέφωνο')}
            name="phone"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput label={t('Κινητό')} name="mobile" placeholder="" disabled={!canEdit} />
          <FieldInput
            label={t('E-mail')}
            name="email"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Α.Φ.Μ.')}
            name="companyVat"
            placeholder=""
            disabled={!canEdit}
            required
          />
          <FieldInput
            label={t('Δ.Ο.Υ.')}
            name="companyTaxOffice"
            placeholder=""
            disabled={!canEdit}
            required
          />
        </Col>
      </Row>
    </>
  );
};

/** *****************************************************
 * Δημόσια φόρμα επικαιροποίησης στοιχείων σημείου — χωρίς login
 * ***************************************************** */
export const CollectionpointUpdateForm = ({
  canEdit,
  submitting,
  submitError,
  saved,
  expiresAt,
  collectionpoint,
  regions,
  subregions,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    name: YupString.required(t(requiredString)),
    addressLine1: YupString.required(t(requiredString)),
    addressLine2: YupString,
    postalCode: YupString.required(t(requiredString)),
    addressArea: YupString.required(t(requiredString)),
    addressCity: YupString.required(t(requiredString)),
    regionId: Yup.string().required(t(requiredString)),
    subregionId: Yup.string().required(t(requiredString)),
    contactPerson: YupString.required(t(requiredString)),
    phone: YupString.required(t(requiredString)),
    mobile: YupString,
    email: YupString.required(t(requiredString)).test(
      'multi-email',
      t('Το email δεν είναι έγκυρο'),
      (val) => {
        const emails = (val ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return emails.length > 0 && emails.every((e) => Yup.string().email().isValidSync(e));
      },
    ),
    companyVat: YupString.required(t(requiredString)),
    companyTaxOffice: YupString.required(t(requiredString)),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={toFormValues(collectionpoint)}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await onSubmit(values);
      }}
    >
      {({ isSubmitting }) => (
        <FForm>
          <p className="mb-3">Παρακαλούμε ελέγξτε και επικαιροποιήστε τα στοιχεία σας.</p>
          {/* Η φόρμα μένει ανοιχτή σε κάθε είσοδο, μέχρι να λήξει η περίοδος ή να κλείσει η ενημέρωση */}
          <Fields canEdit={canEdit} regions={regions} subregions={subregions} />

          {saved && (
            <Alert variant="success" className="mt-3">
              Τα στοιχεία καταχωρήθηκαν.
              {canEdit && (
                <>
                  {' '}
                  Μπορείτε να τα τροποποιήσετε και να τα υποβάλετε ξανά μέχρι τη λήξη της περιόδου
                  {expiresAt ? ` (${formatDate(expiresAt)})` : ''}.
                </>
              )}
            </Alert>
          )}
          {submitError && (
            <Alert variant="danger" className="mt-3">
              {submitError}
            </Alert>
          )}

          {canEdit && (
            <div className="d-flex justify-content-end mt-3">
              {/* Νέα υποβολή όσο είναι ενεργή η περίοδος — και μετά την πρώτη καταχώρηση */}
              <Button type="submit" variant="success" disabled={isSubmitting || submitting}>
                {isSubmitting || submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : saved ? (
                  t('Ενημέρωση στοιχείων')
                ) : (
                  t('Υποβολή στοιχείων')
                )}
              </Button>
            </div>
          )}
        </FForm>
      )}
    </Formik>
  );
};
